import { useState } from 'react'
import { Button, Fa, Text } from '@eltoro-ui/components'
import { Creativeservicev1Creative } from 'next-gen-sdk'
import {
  styleTailwind,
  simplifyEnum,
  getCreativeFaETIcons,
  readableCreativeType,
} from 'Helpers'
import { getThumbnail } from './getThumbnail'
import { ModifiedOrderLineType } from '../types'
import { formatByteString } from '@eltoro-ui/helpers'
import { CreativePreview } from 'Components/CreativePreview'

const HeaderText = styleTailwind(Text, 'EditOLTable__header-text')
const EditOLRowItem = styleTailwind(Text, 'EditOLRowItem__item')
const CreativeTableGrid = styleTailwind(
  'div',
  'CreativeEditOLTable__CreativeTableGrid laptopsmall:flex laptopsmall:flex-wrap laptopsmall:justify-between laptopsmall:gap-y-4',
)
const TabletHeader = styleTailwind(
  HeaderText,
  'EditOLTable__TabletHeader block',
)
const StackBtn = styleTailwind('div', 'StackedButtonIcon group')

export type CreativeSubTableRowProps = {
  creative: Creativeservicev1Creative
  orderLine: ModifiedOrderLineType
  isTablet: boolean
  handleRemoveCreative: (
    orderLine: ModifiedOrderLineType,
    creative?: Creativeservicev1Creative,
  ) => void
}

export const CreativeSubTableRow = ({
  creative,
  orderLine,
  handleRemoveCreative,
  isTablet,
}: CreativeSubTableRowProps) => {
  const [
    previewCreative,
    setPreviewCreative,
  ] = useState<Creativeservicev1Creative | null>(null)

  const getCreativeSize = () => {
    if (creative.files?.[0]) {
      const [file] = creative.files
      return `${file.width || creative.width || 0}x${
        file.height || creative.height || 0
      }`
    }
    return `${creative.width || 0}x${creative.height || 0}`
  }

  const getCreativeStatus = () => {
    if (!creative.status) return 'Unknown'
    return simplifyEnum(2, creative.status, true)
  }

  return (
    <>
      <CreativeTableGrid className="EditOLRowItem CreativeSubTableRow bg-tint-gray-25 border-tint-khaki-300 border-l-4">
        <EditOLRowItem className="EditOLRowItem__creative-name break-words">
          {isTablet && <TabletHeader>Creative Name</TabletHeader>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="CreativeEditOLTable__thumbnail-container bg-tint-gray-50 border-tint-gray-200 hover:border-primary focus:ring-primary flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded border transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
              onClick={() => setPreviewCreative(creative)}
              aria-label="Click to view full size creative"
              data-tooltip="top"
            >
              {getThumbnail(creative, 'xs')}
            </button>
            <div className="flex flex-col gap-1">
              <Text className="CreativeName break-all">
                {creative.name || 'No name'}
              </Text>
              <Text className="text-grey-500 font-mono text-xs">
                {creative.id || 'No ID'}
              </Text>
              {/* uncomment for testing brand url application */}
              {/* <Text className="text-grey-500 font-mono text-xs">
                {(() => {
                  if (
                    orderLine.brandUrlEdited &&
                    orderLine.brandUrls === undefined
                  ) {
                    return 'No brand url (removed)'
                  }
                  if (orderLine.brandUrls && orderLine.brandUrls.length > 0) {
                    return orderLine.brandUrls[0] // Show the first brand URL from order line
                  }
                  return (
                    creative.nativeMetadata?.alternativeMobileLandingPageUrl ||
                    'No brand url'
                  )
                })()}
              </Text> */}
            </div>
          </div>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__creative-type break-words">
          {isTablet && <TabletHeader>Media Type</TabletHeader>}
          <div className="flex items-center gap-2">
            {getCreativeFaETIcons(creative.type)}
            <Text>
              {creative.type ? readableCreativeType(creative.type) : 'Unknown'}
            </Text>
          </div>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__creative-file-type break-words">
          {isTablet && <TabletHeader>File Type</TabletHeader>}
          <Text>
            {(() => {
              const fileName = creative.files?.[0]?.name || creative.name || ''
              const extension = fileName.split('.').pop()?.toUpperCase()
              return extension || 'Unknown'
            })()}
          </Text>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__creative-dimensions break-words">
          {isTablet && <TabletHeader>Dimensions</TabletHeader>}
          <Text>{getCreativeSize()}</Text>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__creative-file-size break-words">
          {isTablet && <TabletHeader>File Size</TabletHeader>}
          <Text>
            {formatByteString(Number(creative.files?.[0]?.size || 0))}
          </Text>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__creative-status break-words">
          {isTablet && <TabletHeader>Status</TabletHeader>}
          <Text>{getCreativeStatus()}</Text>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__creative-delete break-words">
          {isTablet && <TabletHeader>Remove</TabletHeader>}
          <div className="StackedButtonIcon__wrap flex gap-1 self-center">
            <StackBtn
              className="group-hover:text-primary"
              data-tooltip="top"
              aria-label="Remove this Creative from Order Line"
            >
              <Button
                iconCenter={
                  <Fa
                    icon="trash-xmark"
                    size={1}
                    className="StackedButtonIcon__icon"
                  />
                }
                onClick={() => handleRemoveCreative(orderLine, creative)}
              >
                <span className="IconLabel text-s">Remove</span>
              </Button>
            </StackBtn>
          </div>
        </EditOLRowItem>
      </CreativeTableGrid>
      {/* Creative Preview Modal */}
      {previewCreative && previewCreative.files?.[0] && (
        <CreativePreview
          creative={previewCreative}
          file={previewCreative.files[0]}
          offClick={() => setPreviewCreative(null)}
        />
      )}
    </>
  )
}
